import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { motion } from 'framer-motion';
import { Save, Building2, Share2, Info } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_SETTINGS = {
  company_name: '',
  company_tagline: '',
  company_phone: '',
  company_phone_alt: '',
  company_email: '',
  company_address: '',
  company_whatsapp: '',
  social_instagram: '',
  social_linkedin: '',
  social_facebook: '',
  social_youtube: '',
  social_twitter: '',
};

export default function SettingsManager() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`);
      const map = { ...DEFAULT_SETTINGS };
      res.data.forEach(item => {
        if (map.hasOwnProperty(item.key)) {
          map[item.key] = item.value;
        }
      });
      setSettings(map);
      setInitialSettings(map);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (groupKeys) => {
    setSaving(true);
    try {
      const promises = groupKeys.map(key => 
        axios.post(`${API_URL}/settings`, { key, value: settings[key] })
      );
      await Promise.all(promises);
      toast.success('Settings saved successfully!');
      
      // Update initial settings for tracking changes
      const newInitial = { ...initialSettings };
      groupKeys.forEach(k => { newInitial[k] = settings[k]; });
      setInitialSettings(newInitial);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const companyKeys = [
    'company_name', 'company_tagline', 'company_phone', 
    'company_phone_alt', 'company_email', 'company_whatsapp', 'company_address'
  ];
  const socialKeys = [
    'social_instagram', 'social_linkedin', 'social_facebook', 
    'social_youtube', 'social_twitter'
  ];

  const hasCompanyChanges = companyKeys.some(k => settings[k] !== initialSettings[k]);
  const hasSocialChanges = socialKeys.some(k => settings[k] !== initialSettings[k]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Site Settings</h2>
          <p className="text-neutral-400 text-sm">Manage global company information and social media links.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Company Info Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-neutral-800 flex items-center justify-between bg-neutral-900/50">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Building2 size={20} className="text-amber-500" /> Company Information
              </h3>
              {hasCompanyChanges && (
                <span className="text-amber-500 text-xs px-2 py-1 bg-amber-500/10 rounded flex items-center gap-1">
                  <Info size={12}/> Unsaved
                </span>
              )}
            </div>
            
            <div className="p-6 space-y-4 flex-1">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company Name" name="company_name" value={settings.company_name} onChange={handleChange} placeholder="HOFLICH" />
                <Field label="Tagline" name="company_tagline" value={settings.company_tagline} onChange={handleChange} placeholder="Develop Your Land Independently" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Field label="Primary Phone" name="company_phone" value={settings.company_phone} onChange={handleChange} placeholder="+91 98854 94242" />
                <Field label="Secondary Phone" name="company_phone_alt" value={settings.company_phone_alt} onChange={handleChange} placeholder="+91 40 2354 1493" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Email Address" name="company_email" type="email" value={settings.company_email} onChange={handleChange} placeholder="info@hoflich.in" />
                <Field label="WhatsApp Number" name="company_whatsapp" value={settings.company_whatsapp} onChange={handleChange} placeholder="919885494242" />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-1">Office Address</label>
                <textarea 
                  name="company_address"
                  rows={3}
                  value={settings.company_address} 
                  onChange={handleChange}
                  placeholder="Plot No. 95/A, Road No. 44..."
                  className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-neutral-800 bg-neutral-900/50">
              <button 
                onClick={() => handleSave(companyKeys)}
                disabled={!hasCompanyChanges || saving}
                className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition ${
                  hasCompanyChanges && !saving 
                    ? 'bg-amber-500 text-black hover:bg-amber-600' 
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                }`}
              >
                <Save size={18} /> Save Company Info
              </button>
            </div>
          </motion.div>

          {/* Social Links Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-neutral-800 flex items-center justify-between bg-neutral-900/50">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Share2 size={20} className="text-amber-500" /> Social Media Links
              </h3>
              {hasSocialChanges && (
                <span className="text-amber-500 text-xs px-2 py-1 bg-amber-500/10 rounded flex items-center gap-1">
                  <Info size={12}/> Unsaved
                </span>
              )}
            </div>
            
            <div className="p-6 space-y-4 flex-1">
              <Field label="Instagram URL" name="social_instagram" type="url" value={settings.social_instagram} onChange={handleChange} placeholder="https://instagram.com/..." />
              <Field label="LinkedIn URL" name="social_linkedin" type="url" value={settings.social_linkedin} onChange={handleChange} placeholder="https://linkedin.com/company/..." />
              <Field label="Facebook URL" name="social_facebook" type="url" value={settings.social_facebook} onChange={handleChange} placeholder="https://facebook.com/..." />
              <Field label="YouTube URL" name="social_youtube" type="url" value={settings.social_youtube} onChange={handleChange} placeholder="https://youtube.com/..." />
              <Field label="Twitter / X URL" name="social_twitter" type="url" value={settings.social_twitter} onChange={handleChange} placeholder="https://x.com/..." />
            </div>

            <div className="p-6 border-t border-neutral-800 bg-neutral-900/50">
              <button 
                onClick={() => handleSave(socialKeys)}
                disabled={!hasSocialChanges || saving}
                className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition ${
                  hasSocialChanges && !saving 
                    ? 'bg-amber-500 text-black hover:bg-amber-600' 
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                }`}
              >
                <Save size={18} /> Save Social Links
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-400 mb-1">{label}</label>
      <input 
        type={type} 
        name={name}
        value={value} 
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 transition" 
      />
    </div>
  );
}
